import { Grid, Group, Layout, Page, Tree } from "@/comn/components";
import {
    useFetch,
    useForm,
    useGrid,
    useModal,
    usePage,
    useResource,
    useStore,
    useTheme,
    useToast,
    useTree,
} from "@/comn/hooks";
import { comnUtils } from "@/comn/utils";
import {
    APIS,
    SF_MENU_MGMT_SRCH,
    SG_MENU_AUTH_LIST,
    URLS,
} from "@/tz/go/egaz/osci/ptl/ptl/menu/services/OsciMenuService";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const OSCI0104001Q = (): JSX.Element => {
    const { pgeUid } = usePage({
        auth: ["auth_osci0003"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const modal = useModal();
    const toast = useToast();
    const tree = useTree();
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { setStore, getStore } = useStore({ pgeUid: pgeUid });

    // Menu ID for the linked authority list !== 연결 권한 목록 조회 대상 메뉴 ID ==!
    const authMenuIdRef = useRef<string>("");
    // Linked authority list (null: section hidden) !== 메뉴에 연결된 권한 목록 (null 이면 영역 숨김) ==!
    const [menuAuthList, setMenuAuthList] = useState<any>(null);

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0088" },
            { area: "comnCd", comnCd: "OSC0095" },
        ],
    });

    const form = {
        menuMgmtSrch: useForm({ defaultSchema: SF_MENU_MGMT_SRCH }),
    };

    const grid = {
        menuAuthList: useGrid({ defaultSchema: SG_MENU_AUTH_LIST }),
    };

    const fetch = {
        getMenuList: useFetch({
            api: (data: any) => APIS.getMenuList(data),
            onSuccess: (data) => {
                const menuList = data["menuList"].content;
                tree.setData([
                    {
                        name: "ROOT",
                        menuId: "menu_root",
                        menuPath: "ROOT",
                        children: handler.createTreeData(menuList, [], 1, "menu_root"),
                    },
                ]);

                tree.openTree();
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00043" }), // 조회가 실패하였습니다.
        }),
        getMenuAuthList: useFetch({
            api: (menuId: any) => APIS.getMenuAuthList(menuId),
            onSuccess: (data, params) => {
                if (handler.refetchMenuAuthIfChanged(params)) return;
                setMenuAuthList(data?.menuAuthList);
            },
            onError: (error, params) => {
                if (handler.refetchMenuAuthIfChanged(params)) return;
                toast.showToast({ type: "error", content: "msg.osc.00043" }); // 조회가 실패하였습니다.
                handler.clearMenuAuthList();
            },
        }),
    };

    const handler = {
        createTreeData: (orgList: any, newList: any, level: any, menuId: any) => {
            const tempList = orgList
                .map((item: any) => {
                    return {
                        name: comnUtils.replaceEmpty(item.menuNm),
                        checkbox: false,
                        menuId: comnUtils.replaceEmpty(item.menuId),
                        menuNm: comnUtils.replaceEmpty(item.menuNm),
                        menuLvl: comnUtils.replaceEmpty(item.menuLvl, 0),
                        menuOrd: comnUtils.replaceEmpty(item.menuOrd, 0),
                        uprMenuId: comnUtils.replaceEmpty(item.uprMenuId),
                        uprMenuNm: comnUtils.replaceEmpty(item.uprMenuNm),
                        menuPath: comnUtils.replaceEmpty(item.menuPath),
                    };
                })
                .filter((item: any) => item["menuLvl"] === level && item["uprMenuId"] === menuId);

            if (tempList.length === 0) return newList;

            tempList.forEach((item: any) => {
                if (!item.children) item.children = [];
                handler.createTreeData(orgList, item.children, item["menuLvl"] + 1, item["menuId"]);
                newList.push(item);
            });

            return newList;
        },
        onTreeClick: (treeInfo: any) => {
            form.menuMgmtSrch.setValue("menuId", treeInfo.menuId);
            form.menuMgmtSrch.setValue("menuPath", treeInfo.menuPath);
            handler.getMenuAuthList(treeInfo.menuId);
        },
        // Select Authority List Linked to Menu !== 메뉴에 연결된 권한 목록 조회 ==!
        getMenuAuthList: (menuId: any) => {
            // ROOT 는 실제 메뉴가 아니므로 미선택과 동일하게 영역을 숨긴다.
            if (comnUtils.isEmpty(menuId) || menuId === "menu_root") {
                handler.clearMenuAuthList();
                return;
            }

            authMenuIdRef.current = menuId;
            fetch.getMenuAuthList.fetch(menuId);
        },
        // Re-query if the selected menu changed while loading !== 조회 중 선택 메뉴가 바뀐 경우 최신 메뉴로 재조회 ==!
        refetchMenuAuthIfChanged: (params: any) => {
            const latestMenuId = authMenuIdRef.current;
            if (params?.[0] === latestMenuId) return false;

            if (!comnUtils.isEmpty(latestMenuId)) fetch.getMenuAuthList.fetch(latestMenuId);
            return true;
        },
        // Clear and hide Linked Authority List !== 연결 권한 목록 초기화(영역 숨김) ==!
        clearMenuAuthList: () => {
            authMenuIdRef.current = "";
            setMenuAuthList(null);
        },
        onMenuNewBtnClick: () => {
            const formMenuId = form.menuMgmtSrch.getValue("menuId"); // Target Upper Menu ID
            const url = URLS.osci0104002s;
            const trgtUrl = comnUtils.isEmpty(formMenuId) ? url : `${url}/${formMenuId}`;
            navigate(`${trgtUrl}`);
        },
        onEditBtnClick: () => {
            const formMenuId = form.menuMgmtSrch.getValue("menuId");
            console.log(formMenuId);
            if (formMenuId === "ROOT" || formMenuId === "menu_root") {
                modal.openModal({ content: "msg.osc.00200" }); // You cannot modify the Root menu. !== Root 메뉴는 수정할 수 없습니다. ==!
                return;
            }

            if (comnUtils.isEmpty(formMenuId)) {
                modal.openModal({ content: "msg.osc.00196" }); // Please select the menu. !== 메뉴를 선택하세요. ==!
                return;
            }

            navigate(`${URLS.osci0104003s}/${formMenuId}`);
        },
        onSearchBtnClick: () => {
            form.menuMgmtSrch.setValue("menuId", "");
            form.menuMgmtSrch.setValue("menuPath", "");
            handler.clearMenuAuthList();

            setStore(pgeUid, form.menuMgmtSrch.getValues());

            form.menuMgmtSrch.handleSubmit(
                () => {
                    fetch.getMenuList.fetch(form.menuMgmtSrch.getValues());
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // 데이터가 유효하지 않습니다.
                },
            )();
        },
        onClear: () => {
            form.menuMgmtSrch.clearValues();
            form.menuMgmtSrch.setValues({ bsopClsfCd: "OSC", ptlTpCd: "O" });
            tree.setData([]);
            handler.clearMenuAuthList();
        },
    };

    useEffect(() => {
        // Service Category, Portal Type 은 고정값이므로 수정할 수 없도록 한다.
        form.menuMgmtSrch.setEditable("bsopClsfCd", false);
        form.menuMgmtSrch.setEditable("ptlTpCd", false);

        const store = getStore(pgeUid);
        if (!comnUtils.isEmpty(store)) {
            form.menuMgmtSrch.setValue("bsopClsfCd", store.bsopClsfCd);
            form.menuMgmtSrch.setValue("ptlTpCd", store.ptlTpCd);
        }

        if (
            comnUtils.isEmpty(form.menuMgmtSrch.getValue("bsopClsfCd")) ||
            comnUtils.isEmpty(form.menuMgmtSrch.getValue("ptlTpCd"))
        ) {
            return;
        }
        handler.onSearchBtnClick();
    }, [theme.lang]);

    const menuAuthCnt = menuAuthList?.content?.length ?? 0;

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.menuMgmtSrch.schema.bsopClsfCd} />
                            <Group.Control {...form.menuMgmtSrch.schema.ptlTpCd} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Layout.Button role={"reset"} onClick={() => handler.onClear()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Layout.Button role={"search"} onClick={() => handler.onSearchBtnClick()} />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>

            <Group>
                <Group.Header>
                    <Group.Title title={"T_MENU_LIST"} />
                </Group.Header>
                <Group.Body>
                    <Tree {...tree.tree} size={12} height={300} onClick={handler.onTreeClick} checkbox={false} />
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.menuMgmtSrch.schema.menuPath} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Left>
                    <Layout.Button role={"new"} onClick={handler.onMenuNewBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"edit"} onClick={handler.onEditBtnClick} />
                </Layout.Right>
            </Layout>

            {/* Linked Authority List !== 메뉴에 연결된 권한 목록 ==! */}
            {menuAuthList && (
                <Group>
                    <Group.Header>
                        <Group.Title title={`${t("T_LINK_AUTH_LIST")} (${menuAuthCnt})`} />
                    </Group.Header>
                    <Group.Body>
                        <Group.Section>
                            {menuAuthCnt > 0 ? (
                                <Grid {...grid.menuAuthList.grid} data={menuAuthList} />
                            ) : (
                                <Group.Row>
                                    <Group.Any anySize={12} align="left">
                                        <span className="text-sm text-gray-500">{t("L_NO_LINK_AUTH")}</span>
                                    </Group.Any>
                                </Group.Row>
                            )}
                        </Group.Section>
                    </Group.Body>
                </Group>
            )}
        </Page>
    );
};
